import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Pool } from '@/types';
import { MOCK_POOLS } from '@/data/mockData';

interface PoolStore {
  pools: Pool[];
  selectedPoolIds: string[];
  isLoading: boolean;
  error: string | null;

  fetchPools: () => Promise<void>;
  togglePool: (poolId: string) => void;
  selectAll: () => void;
  getSelectedPool: () => Pool | undefined;
  getSelectedPools: () => Pool[];
  getPoolById: (poolId: string) => Pool | undefined;
}

export const usePoolStore = create<PoolStore>()(
  persist(
    (set, get) => ({
      pools: [],
      selectedPoolIds: [],
      isLoading: false,
      error: null,

      fetchPools: async () => {
        // Mock data — will be replaced when PoolManagement service is available
        const pools = MOCK_POOLS;
        set(state => {
          const validIds = new Set(pools.map(p => p.poolId));
          const validSelected = state.selectedPoolIds.filter(id => validIds.has(id));
          const selectedPoolIds =
            validSelected.length > 0 ? validSelected : pools[0] ? [pools[0].poolId] : [];
          return { pools, isLoading: false, selectedPoolIds };
        });
      },

      togglePool: (poolId: string) => {
        set(state => {
          const exists = state.selectedPoolIds.includes(poolId);
          if (exists) {
            return { selectedPoolIds: state.selectedPoolIds.filter(id => id !== poolId) };
          }
          return { selectedPoolIds: [...state.selectedPoolIds, poolId] };
        });
      },

      selectAll: () => {
        set(state => {
          const allSelected = state.selectedPoolIds.length === state.pools.length;
          return { selectedPoolIds: allSelected ? [] : state.pools.map(p => p.poolId) };
        });
      },

      getSelectedPool: () => {
        const { pools, selectedPoolIds } = get();
        return pools.find(p => p.poolId === selectedPoolIds[0]);
      },

      getSelectedPools: () => {
        const { pools, selectedPoolIds } = get();
        const idSet = new Set(selectedPoolIds);
        return pools.filter(p => idSet.has(p.poolId));
      },

      getPoolById: (poolId: string) => {
        return get().pools.find(p => p.poolId === poolId);
      },
    }),
    {
      name: 'w365a-pool',
      partialize: state => ({ selectedPoolIds: state.selectedPoolIds }),
    }
  )
);
